import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { INewsFeedData, NewsFeedComponent } from '../../../news-feed/news-feed/news-feed.component';
import { LoadingService } from '../../../shared/fi-loading/loading.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { NewsFeedService } from '../../../news-feed/news-feed.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { isDefined } from '@fi-shared/utils/is-defined';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { BehaviorSubject, Subject } from 'rxjs';
import { WidgetPlaceHolderControlModel } from '@fi-shared/widget/widget-place-holder-control.model';
import { takeUntil } from 'rxjs/operators';
import { WidgetSharedService } from '@fi-shared/widget/widget-shared.service';
import { INewsFeedModel } from '@fi-shared/fi-news-feed-shared/fi-news-feed-shared';

@Component({
    selector: 'fi-widget-content-news-feed',
    templateUrl: './news-feed.component.html',
    styleUrls: ['./news-feed.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-widget-content-news-feed' },
    standalone: false,
})
export class WidgetContentNewsFeedComponent implements OnInit {
    @Input() control: WidgetPlaceHolderControlModel;

    @ViewChild(NewsFeedComponent, { static: false }) newsFeedComponent: NewsFeedComponent;

    newsFeedData: INewsFeedData;
    initialNewsFeedResponse: INewsFeedModel[];

    private _isEmpty$ = new BehaviorSubject<boolean>(true);
    get isEmpty$() {
        return this._isEmpty$.asObservable();
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private appTextService: AppTextService,
        private fiLoadingService: LoadingService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private newsFeedService: NewsFeedService,
        private widgetSharedService: WidgetSharedService,
        private fiSignalRHelperService: FiSignalRHelperService,
        private stateService: FiStateService,
    ) {}

    ngOnInit() {
        this.initFeed();

        this.widgetSharedService
            .getWidgetSettingsChanged$(() => this.control.Settings)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((data) => {
                this.cdr.markForCheck();
                if (!data || !data.skipReloadData) {
                    this.initFeed();
                }
            });
    }

    initFeed() {
        const newsFeedData: INewsFeedData = {
            showForumActivity: this.control.Settings.ShowForumActivity,
            newsFeedHeaderText: this.appTextService.get('posts'),
        };

        const showForumActivityInNewsFeed = isDefined(newsFeedData.showForumActivity)
            ? newsFeedData.showForumActivity
            : true;
        this.fiSignalRHelperService.showForumActivityInNewsFeed = showForumActivityInNewsFeed;

        let promise: Promise<any>;
        let isFromState = false;
        if (
            this.stateService.current.name == FiStateNames.Home.Post._name ||
            this.stateService.current.name == FiStateNames.Home.Post.Comment
        ) {
            newsFeedData.objectId = this.stateService.params.postId;
            newsFeedData.objectType = this.stateService.params.objectType;
            newsFeedData.commentId = this.stateService.params.commentId;
            promise = this.newsFeedService.GetNewsFeedsToPost(
                this.stateService.params.postId,
                this.stateService.params.objectType,
            );
        }
        // FI-206 Feed improvement - remembering where you were
        // load posts to saved id
        else if (
            this.stateService.current.name == FiStateNames.Home._name &&
            window.history.state &&
            window.history.state.remember
        ) {
            isFromState = true;
            newsFeedData.objectId = window.history.state.remember.objectId;
            newsFeedData.objectType = window.history.state.remember.objectType;
            newsFeedData.noHighlight = true;
            promise = this.newsFeedService.GetNewsFeedsToPost(
                window.history.state.remember.objectId,
                window.history.state.remember.objectType,
            );
        } else {
            promise = this.newsFeedService.GetNewsFeeds(0, newsFeedData);
        }

        this.fiLoadingService.startLoading();
        promise.then(
            (initialNewsFeedResponse) => {
                this._isEmpty$.next(false);
                this.initialNewsFeedResponse = initialNewsFeedResponse;
                this.newsFeedData = newsFeedData;
                if (initialNewsFeedResponse.length == 0) {
                    this.fiLoadingService.finishLoading();
                    if (isFromState) {
                        // remembered element not found, get newsfeeds from start
                        delete window.history.state.remember;
                        this.initFeed();
                    }
                }
            },
            () => this.fiLoadingService.finishLoading(),
        );
    }

    onInitialRenderDone() {
        this.fiLoadingService.finishLoading();
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
